import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Building2, Save } from "lucide-react";
import FinanceLayout from "@/components/FinanceLayout";
import { useWorkspace } from "@/context/WorkspaceContext";
import { api, formatApiError } from "@/lib/api";
import { EXPENSE_ITEMS } from "@/lib/finance";
import { toast } from "sonner";

const label = "text-sm font-semibold text-slate-900 mb-2";
const input = "w-full h-12 rounded-xl border border-slate-200 bg-slate-50/40 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500 placeholder:text-slate-400";

export default function RecurringExpenseNew() {
  const { workspace } = useWorkspace();
  const nav = useNavigate();
  const [firms, setFirms] = useState([]);
  const [extraItems, setExtraItems] = useState([]);
  const [f, setF] = useState({ name: "", item: "", item_other_on: false, item_other: "", amount: "", firm: "", is_business: false, other_note: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { (async () => {
    const [{ data }, { data: ex }] = await Promise.all([
      api.get("/finance/firms", { params: { workspace } }),
      api.get("/finance/lookups/expense_items", { params: { workspace } }),
    ]);
    setFirms(data);
    setExtraItems(ex);
  })(); }, [workspace]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.name.trim() || !f.amount) return setError("Gider Adı ve Tutar zorunlu.");
    if (f.is_business && !f.other_note.trim()) return setError("İşletme gideri seçildiğinde 'Diğer' açıklama alanı zorunludur.");
    setSaving(true);
    try {
      if (f.item_other_on && f.item_other.trim()) {
        await api.post("/finance/lookups/expense_items", null, { params: { workspace, label: f.item_other.trim() } });
      }
      await api.post("/finance/recurring-expenses", {
        workspace,
        name: f.name,
        item: f.item_other_on ? f.item_other : f.item,
        amount: parseFloat(f.amount),
        firm: f.is_business ? "isletme" : (f.firm || ""),
        is_business: f.is_business,
        other_note: f.other_note,
        active: true,
      });
      toast.success("Sürekli gider eklendi");
      nav("/finance/monthly-expenses");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <FinanceLayout>
      <div className="mb-6 flex items-center gap-3">
        <button onClick={() => nav(-1)} className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50"><ArrowLeft size={16} /></button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Sürekli Gider Ekleme</h1>
          <p className="text-sm text-slate-500 mt-1">İşletmeniz için sürekli gider tanımı oluşturun.</p>
        </div>
      </div>

      <form onSubmit={submit} className="max-w-[720px] mx-auto rounded-2xl border border-slate-100 bg-white p-8" data-testid="re-form">
        <div className="space-y-6">
          <div>
            <div className={label}>Gider Adı</div>
            <input type="text" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Maaşlar, Yapay Zeka Araçları, Kira..." className={input} data-testid="re-name" />
          </div>
          <div>
            <div className={label}>Gider Kalemi</div>
            <select value={f.item} onChange={(e) => setF({ ...f, item: e.target.value })} disabled={f.item_other_on} className={input + (f.item_other_on ? " opacity-50" : "")} data-testid="re-item">
              <option value="">Gider kalemi seçiniz</option>
              {EXPENSE_ITEMS.map((it) => <option key={it} value={it}>{it}</option>)}
              {extraItems.filter((it) => !EXPENSE_ITEMS.includes(it)).map((it) => <option key={it} value={it}>{it}</option>)}
            </select>
            <label className="mt-2 flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
              <input type="checkbox" checked={f.item_other_on} onChange={(e) => setF({ ...f, item_other_on: e.target.checked })} data-testid="re-item-other-toggle" className="w-4 h-4 accent-emerald-600" />
              Diğer gider kalemi
            </label>
            {f.item_other_on && (
              <input type="text" value={f.item_other} onChange={(e) => setF({ ...f, item_other: e.target.value })} placeholder="Yeni gider kalemi (kaydedildiğinde listeye eklenir)" className={input + " mt-2"} data-testid="re-item-other" />
            )}
          </div>
          <div>
            <div className={label}>Tutar</div>
            <div className="relative">
              <input type="number" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="Aylık tutar giriniz" className={input + " pr-10"} data-testid="re-amount" />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₺</span>
            </div>
          </div>
          <div>
            <div className={label}>Firma</div>
            <select value={f.firm} onChange={(e) => setF({ ...f, firm: e.target.value })} disabled={f.is_business} className={input + (f.is_business ? " opacity-60" : "")} data-testid="re-firm">
              <option value="">Firma seçiniz</option>
              {firms.map((fb) => <option key={fb} value={fb}>{fb}</option>)}
            </select>
            <div className="text-xs text-slate-500 mt-1.5">İşletme gideri seçildiğinde diğer alanı zorunludur.</div>
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-100 p-4">
            <label className="flex items-center justify-between cursor-pointer">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-md bg-white flex items-center justify-center text-emerald-700"><Building2 size={16} /></div>
                <span className="text-sm font-semibold text-slate-800">İşletme Gideri</span>
              </div>
              <span className={`relative w-11 h-6 rounded-full transition-colors ${f.is_business ? "bg-emerald-600" : "bg-slate-300"}`}>
                <input type="checkbox" checked={f.is_business} onChange={(e) => setF({ ...f, is_business: e.target.checked })} className="opacity-0 absolute inset-0 w-full h-full cursor-pointer" data-testid="re-business-toggle" />
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${f.is_business ? "left-5" : "left-0.5"}`} />
              </span>
            </label>
            {f.is_business && (
              <div className="mt-4">
                <div className="text-sm font-medium text-slate-800 mb-2">Diğer (Açıklama)</div>
                <input type="text" value={f.other_note} onChange={(e) => setF({ ...f, other_note: e.target.value })} placeholder="Gider adını belirtiniz" className={input} data-testid="re-other" />
              </div>
            )}
          </div>
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3" data-testid="re-error">{error}</div>}
          <button type="submit" disabled={saving} data-testid="re-save" className="w-full rounded-xl bg-emerald-800 hover:bg-emerald-700 text-white h-12 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 mt-8">
            <Save size={15} /> {saving ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </div>
      </form>
    </FinanceLayout>
  );
}
